import html2canvas from "html2canvas";
import api from "@/utils/axios-utils";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { HOISTED_ATTRIBUTE } from "@/features/tutor/vo-demo/utils/hoistSlideStylesheets";
import {
  addCaptureCssToClone,
  collectSlideCaptureCss,
} from "@/features/tutor/utils/slideCaptureCss";
import {
  applyBlobImagesToClone,
  applyBlobImagesToCss,
  loadSlideImagesPastCache,
  revokeCaptureImageUrls,
  type CaptureImageUrls,
} from "@/features/tutor/utils/slideCaptureImages";
import { withSvgStyleCopyLimited } from "@/features/tutor/utils/slideCaptureSvgStyles";
import { debugWarn } from "@/utils/debugLog";

const SLIDE_W = 960;
const SLIDE_H = 700;
// html2canvas awaits image decodes with no timeout of its own, and every caller
// blocks on this before navigating. Cap it so a stuck decode cannot pin them.
const CAPTURE_TIMEOUT_MS = 3000;
// Past either wait the capture goes ahead without whatever was late. The font
// wait runs inside CAPTURE_TIMEOUT_MS; the image load runs before it starts.
const IMAGE_LOAD_TIMEOUT_MS = 1000;
const FONT_WAIT_MS = 1000;

let capturing = false;

/** One run of the capture, so a timed-out run knows it no longer owns anything. */
type CaptureRun = { abandoned: boolean };

/** What the clone gets besides the slide itself. */
export type SlideCapturePlan = {
  // null: the clone keeps every <head> stylesheet, as html2canvas does.
  captureCss: string | null;
  blobImageUrls: CaptureImageUrls;
};

/**
 * Capture the current slide as a WebP image via html2canvas and upload to S3.
 *
 * Reveal.js applies `transform: scale()` on `.slides` to fit 960×700 content
 * into the viewport. We use html2canvas's `onclone` callback to neutralise
 * the transform on the cloned DOM only — the live DOM is never mutated.
 *
 * The returned promise resolves once html2canvas has rasterised the *current*
 * DOM, or CAPTURE_TIMEOUT_MS after html2canvas's synchronous clone, whichever
 * comes first — at most IMAGE_LOAD_TIMEOUT_MS later for the image load before
 * it. Callers that are about to navigate (change bite, reset notepad, end
 * lesson) MUST await it —
 * otherwise the transition mutates/clears the DOM before the snapshot is taken
 * and we capture a blank slide. The S3 upload itself stays fire-and-forget
 * after rasterisation completes.
 *
 * On timeout the run is abandoned rather than merely released: the caller
 * navigates immediately after, so html2canvas may still be reading a DOM that
 * has moved on to the next slide. Its upload is dropped and the in-flight flag
 * is handed back, so one slow capture costs one screenshot and not two.
 *
 * Skips if a capture is already in flight. Never throws.
 */
export async function captureAndUploadSlideScreenshot(
  slideIdx?: number,
): Promise<void> {
  if (capturing) return;

  const sessionId = useSessionStore.getState().getSessionId();
  const resolvedSlideIdx =
    slideIdx ?? useSlideStore.getState().currentSlideIndex;
  if (!sessionId) return;

  const revealEl = document.querySelector<HTMLElement>(".reveal");
  if (!revealEl) return;

  capturing = true;
  const run: CaptureRun = { abandoned: false };
  // Bounded on its own, and outside the clock below: the clock is for the
  // html2canvas waits that have no bound of their own.
  const blobImageUrls = await loadSlideImagesPastCache(
    revealEl,
    IMAGE_LOAD_TIMEOUT_MS,
  );

  await new Promise<void>((resolve) => {
    // doCapture runs html2canvas's clone synchronously, before its first await,
    // so the clock starts after it. It bounds the waits that can hang (iframe
    // load, image decodes); on an iPad the clone alone used to outlast it.
    const capture = doCapture(
      revealEl,
      blobImageUrls,
      sessionId,
      resolvedSlideIdx,
      run,
    );
    const timerId = setTimeout(() => {
      run.abandoned = true;
      capturing = false;
      resolve();
    }, CAPTURE_TIMEOUT_MS);
    const settle = () => {
      clearTimeout(timerId);
      // Only if we still own it — an abandoned run's replacement may already
      // have raised the flag for itself.
      if (!run.abandoned) capturing = false;
      resolve();
    };
    capture.then(settle, settle);
  });
}

/**
 * html2canvas clones the whole document but draws only `.reveal`. Keep <head>
 * and the path to and inside `.reveal`; skip the rest, and the slide's hoisted
 * font <link> (~2.4s on an iPad). Its hoisted <style> places the sections.
 */
function isOffCapturePath(el: Element, revealEl: Element): boolean {
  if (el.hasAttribute(HOISTED_ATTRIBUTE)) return el.tagName === "LINK";
  if (el.closest("head")) return false;
  return !el.contains(revealEl) && !revealEl.contains(el);
}

function isHeadStylesheet(el: Element): boolean {
  if (el.parentElement !== document.head) return false;
  return (
    el.tagName === "STYLE" ||
    (el.tagName === "LINK" && (el as HTMLLinkElement).rel === "stylesheet")
  );
}

/**
 * The slide's own CSS for the clone, or null to fall back to every <head>
 * stylesheet — slower, but it is what the capture always used.
 */
function collectCaptureCssOrFallback(revealEl: Element): string | null {
  try {
    return collectSlideCaptureCss(revealEl).css;
  } catch (err) {
    debugWarn("[snapshot] capture CSS failed, cloning every stylesheet:", err);
    return null;
  }
}

/**
 * Rasterises `.reveal` at 960×700.
 *
 * Input:   the live `.reveal` element and what the clone gets besides it
 * Output:  the canvas
 * ORDER MATTERS: html2canvas clones synchronously inside the call; that part
 *          freezes the page, so everything slow is prepared before it.
 */
export function renderSlideCanvas(
  revealEl: HTMLElement,
  plan: SlideCapturePlan,
): Promise<HTMLCanvasElement> {
  const { captureCss, blobImageUrls } = plan;
  return withSvgStyleCopyLimited(() =>
    html2canvas(revealEl, {
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#ffffff",
      scale: 1,
      width: SLIDE_W,
      height: SLIDE_H,
      logging: false,
      // Neutralise Reveal.js viewport scaling on the cloned DOM (not the live DOM)
      onclone: (clonedDoc, clonedReveal) => {
        const clonedSlides = clonedReveal.querySelector<HTMLElement>(".slides");
        clonedReveal.style.cssText += `; width: ${SLIDE_W}px !important; height: ${SLIDE_H}px !important; overflow: hidden !important;`;
        if (clonedSlides) {
          clonedSlides.style.cssText += `; transform: none !important; width: ${SLIDE_W}px !important; height: ${SLIDE_H}px !important; inset: 0 !important; position: absolute !important;`;
        }
        applyBlobImagesToClone(clonedReveal, blobImageUrls);
        if (captureCss === null) return;
        return addCaptureCssToClone(
          clonedDoc,
          applyBlobImagesToCss(captureCss, blobImageUrls),
          FONT_WAIT_MS,
        );
      },
      // Ignore elements that shouldn't appear in snapshot
      ignoreElements: (el) => {
        if (captureCss !== null && isHeadStylesheet(el)) return true;
        if (isOffCapturePath(el, revealEl)) return true;
        if (el.classList?.contains("controls")) return true;
        if (el.classList?.contains("progress")) return true;
        if (el.classList?.contains("slide-number")) return true;
        if (el.classList?.contains("backgrounds")) return true;
        return false;
      },
    }),
  );
}

async function doCapture(
  revealEl: HTMLElement,
  blobImageUrls: CaptureImageUrls,
  sessionId: string,
  resolvedSlideIdx: number,
  run: CaptureRun,
): Promise<void> {
  try {
    const canvas = await renderSlideCanvas(revealEl, {
      captureCss: collectCaptureCssOrFallback(revealEl),
      blobImageUrls,
    });

    // Abandoned while rasterising: the caller has navigated, so these pixels
    // are no longer trustworthy as a record of resolvedSlideIdx.
    if (run.abandoned) return;

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const form = new FormData();
        form.append("file", blob, "snapshot.webp");
        api
          .post(
            `/analytics/sessions/${sessionId}/slides/${resolvedSlideIdx}/screenshot`,
            form,
          )
          .catch((err) => {
            debugWarn("[snapshot] upload failed:", err);
          });
      },
      "image/webp",
      0.92,
    );
  } catch (err) {
    debugWarn("[snapshot] capture failed:", err);
  } finally {
    revokeCaptureImageUrls(blobImageUrls);
  }
}
